/**
 * InviteUserDialog — Phase 7 extract from pages/Users.jsx.
 *
 * Self-contained — owns its form state, validation, submit, and the
 * post-create "copy link" success view. Re-fetches users + invites via
 * `onSuccess()` from the parent.
 */
import React, { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
    Mail, Copy, CheckCircle2, AlertCircle, ShieldCheck, Building2,
} from "lucide-react";
import { toast } from "sonner";
import { ROLE_OPTIONS_FOR, EMAIL_RE, BLANK_INVITE } from "@/components/users/constants";
import { fetchAssignableRoles } from "@/lib/roles-registry";
import { roleLabel } from "@/lib/terminology";
import {
    SsoProviderIcon, ssoProviderLabel, fetchTenantSsoProviders,
} from "@/lib/sso-providers";

export default function InviteUserDialog({
    open, onOpenChange, me, users, orgs, tenantsByOrg, loadTenantsForOrg, onSuccess,
}) {
    const [form, setForm] = useState(BLANK_INVITE);
    const [errors, setErrors] = useState({});
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    // iter-120 round-4 follow-up #12 — Per-tenant enabled SSO providers
    // so the picker only offers what the tenant actually has set up.
    const [enabledProviders, setEnabledProviders] = useState([]);
    // Inline domain-policy check — mirrors the Signup flow. Debounced call
    // to /auth/signup/check-email so we can surface a live warning and
    // disable "Send invitation" for non-work / blocked domains.
    const [domainOk, setDomainOk] = useState(null);   // null = unchecked
    const [domainReason, setDomainReason] = useState("");
    const [domainChecking, setDomainChecking] = useState(false);

    // Reset when re-opened
    React.useEffect(() => {
        if (open) {
            setForm(BLANK_INVITE); setErrors({}); setResult(null);
            setDomainOk(null); setDomainReason(""); setDomainChecking(false);
            setEnabledProviders([]); setSsoTarget(null);
        }
    }, [open]);

    // SSO default — resolve the enabled SSO providers of the SELECTED
    // target tenant (its own SSO config only, no platform fallback) and
    // default "Require SSO sign-in" ON when ≥1 is active, OFF otherwise.
    // Re-runs whenever the organization / tenant selection changes; the
    // inviter can still switch it off manually afterwards.
    const [ssoTarget, setSsoTarget] = useState(null);   // tenant id the providers belong to
    const [ssoLoading, setSsoLoading] = useState(false);
    const ssoReq = React.useRef(0);
    // iter-2026 Role registry — fetch assignable roles for the target
    // org so custom roles created in Role Management appear instantly.
    const [assignable, setAssignable] = useState(null);
    React.useEffect(() => {
        if (!open) return;
        const orgId = me?.role === "super_admin" ? form.org_id : me?.org_id;
        fetchAssignableRoles(orgId || null)
            .then((rows) => {
                let visible = rows;
                if (me?.role !== "super_admin") visible = rows.filter((r) => r.code !== "super_admin");
                setAssignable(visible);
                // If the currently selected role was removed by the new
                // availability window, snap back to viewer.
                if (visible.length && !visible.find((r) => r.code === form.role)) {
                    setForm((f) => ({ ...f, role: visible.find((r) => r.code === "viewer") ? "viewer" : visible[0].code }));
                }
            })
            .catch(() => setAssignable(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, form.org_id, me?.role, me?.org_id]);
    const needsTenantPick = me?.role === "super_admin" || me?.role === "org_admin";
    React.useEffect(() => {
        if (!open) return;
        const tenantId = needsTenantPick ? form.tenant_id : "own";
        const reqId = ++ssoReq.current;
        if (!tenantId) {
            setEnabledProviders([]); setSsoTarget(null); setSsoLoading(false);
            setForm((f) => (f.sso_required || f.sso_provider ? { ...f, sso_required: false, sso_provider: "" } : f));
            return;
        }
        setSsoLoading(true);
        // Never keep the previous tenant's ON state while the new
        // tenant's SSO status is still loading.
        setEnabledProviders([]); setSsoTarget(null);
        setForm((f) => (f.sso_required || f.sso_provider ? { ...f, sso_required: false, sso_provider: "" } : f));
        fetchTenantSsoProviders({
            orgId: me?.role === "super_admin" ? form.org_id : undefined,
            tenantId: needsTenantPick ? tenantId : undefined,
        }).then((providers) => {
            if (reqId !== ssoReq.current) return;   // stale response
            setEnabledProviders(providers);
            setSsoTarget(tenantId);
            setSsoLoading(false);
            setForm((f) => ({
                ...f,
                sso_required: providers.length > 0,
                sso_provider: providers.length === 1 ? providers[0] : "",
            }));
            setErrors((e) => (e.sso_provider ? { ...e, sso_provider: null } : e));
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, form.org_id, form.tenant_id, me?.role, me?.tenant_id]);

    // Debounced email-domain check (fires 350ms after the admin stops typing).
    React.useEffect(() => {
        const e = (form.email || "").trim().toLowerCase();
        if (!EMAIL_RE.test(e)) {
            setDomainOk(null); setDomainReason(""); setDomainChecking(false);
            return;
        }
        setDomainChecking(true);
        const t = setTimeout(async () => {
            try {
                const r = await api.get(`/auth/signup/check-email?email=${encodeURIComponent(e)}`);
                setDomainOk(!!r.data?.allowed);
                setDomainReason(r.data?.reason || "");
            } catch {
                // Network blip — don't block; the backend /invite is the source of truth.
                setDomainOk(null); setDomainReason("");
            } finally {
                setDomainChecking(false);
            }
        }, 350);
        return () => clearTimeout(t);
    }, [form.email]);

    const validate = () => {
        const errs = {};
        if (!form.name?.trim()) errs.name = "Full name is required";
        const email = form.email?.trim();
        if (!email) errs.email = "Email is required";
        else if (!EMAIL_RE.test(email)) errs.email = "Enter a valid email address (e.g. name@company.com)";
        else if (users.some((u) => u.email.toLowerCase() === email.toLowerCase())) errs.email = "A user with this email already exists";
        else if (domainOk === false) errs.email = domainReason || "This email domain is not allowed.";
        if (me?.role === "super_admin") {
            if (!form.org_id) errs.org_id = "Select an organization";
            if (!form.tenant_id) errs.tenant_id = "Select a tenant";
        } else if (me?.role === "org_admin") {
            if (!form.tenant_id) errs.tenant_id = "Select a tenant";
        }
        // iter-120 round-4 follow-up #12 — Provider is required when
        // SSO is on. If only one provider is enabled we auto-pin it
        // (no error). If multiple are enabled, force a pick.
        if (form.sso_required) {
            if (enabledProviders.length === 0) {
                errs.sso_provider = "No SSO provider is configured for this entity. Configure one in Settings → SSO first.";
            } else if (!form.sso_provider) {
                if (enabledProviders.length === 1) {
                    form.sso_provider = enabledProviders[0]; // auto-pin
                } else {
                    errs.sso_provider = "Pick which SSO provider this user should sign in with.";
                }
            }
        }
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const submit = async () => {
        if (!validate()) return;
        setBusy(true);
        try {
            const payload = {
                email: form.email.trim().toLowerCase(),
                name: form.name.trim(),
                role: form.role,
                sso_required: !!form.sso_required,
                sso_provider: form.sso_required ? (form.sso_provider || null) : null,
            };
            if (me?.role === "super_admin") {
                payload.org_id = form.org_id;
                payload.tenant_id = form.tenant_id;
            } else if (me?.role === "org_admin") {
                payload.tenant_id = form.tenant_id;
            }
            const r = await api.post("/auth/invite", payload);
            setResult(r.data);
            if (r.data.email_sent) {
                toast.success(`Invitation emailed to ${form.email}`);
            } else {
                toast.warning("Invitation created — email could not be sent. Copy the link below and share it manually.");
            }
            onSuccess?.();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (typeof detail === "string" && detail.toLowerCase().includes("already")) {
                setErrors({ email: detail });
            } else {
                toast.error(detail || "Failed to create invitation");
            }
        } finally {
            setBusy(false);
        }
    };

    const handleOpenChange = (o) => { onOpenChange(o); if (!o) setResult(null); };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto p-5 sm:p-6" data-testid="invite-user-dialog">
                <DialogHeader><DialogTitle>Invite new user</DialogTitle></DialogHeader>
                {result ? (
                    <div className="space-y-4">
                        <div className={`p-4 rounded-sm border ${result.email_sent ? "border-primary/30 bg-primary/5" : "border-border bg-secondary/40"}`}>
                            <div className="flex items-start gap-2.5">
                                {result.email_sent
                                    ? <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                                    : <AlertCircle className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />}
                                <div className="flex-1 min-w-0">
                                    <div className="eyebrow mb-1.5">
                                        {result.email_sent ? "Invitation emailed" : "Invitation ready — email not sent"}
                                    </div>
                                    {result.email_sent ? (
                                        <p className="text-xs text-muted-foreground leading-relaxed">
                                            <b className="font-mono">{result.email}</b> will receive an email with an acceptance link valid for <b>7 days</b>. They now appear in the <b>Pending invitations</b> tab below.
                                        </p>
                                    ) : (
                                        <p className="text-xs text-muted-foreground leading-relaxed">
                                            SMTP isn’t configured (or delivery failed). Copy the link below and share it with <b className="font-mono">{result.email}</b> manually. The invitee now appears in <b>Pending invitations</b>.
                                            {result.email_detail && result.email_detail !== "sent" && (
                                                <span className="block mt-1.5 font-mono text-[10px] text-muted-foreground/70 break-all">{result.email_detail}</span>
                                            )}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div>
                            <label className="eyebrow mb-1.5 block">Acceptance link · valid 7 days</label>
                            <div className="flex items-stretch gap-2">
                                <div className="flex-1 min-w-0 border border-border rounded-sm bg-background p-2.5 flex items-start gap-2">
                                    <Mail className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
                                    <code className="text-[11px] font-mono break-all leading-snug flex-1 min-w-0 select-all" data-testid="invite-accept-url">
                                        {result.accept_url}
                                    </code>
                                </div>
                                <Button
                                    variant="outline"
                                    className="rounded-sm h-auto px-3 shrink-0 self-stretch gap-1.5"
                                    onClick={() => { navigator.clipboard.writeText(result.accept_url); toast.success("Link copied to clipboard"); }}
                                    data-testid="invite-copy-link"
                                >
                                    <Copy className="h-3.5 w-3.5" />
                                    <span className="text-xs">Copy</span>
                                </Button>
                            </div>
                        </div>
                        <DialogFooter className="pt-2">
                            <Button onClick={() => handleOpenChange(false)} className="rounded-sm" data-testid="invite-close">Done</Button>
                        </DialogFooter>
                    </div>
                ) : (
                    <>
                        <div className="space-y-3">
                            <div>
                                <label className="eyebrow mb-1.5 block">Full name</label>
                                <input value={form.name}
                                    onChange={(e) => { setForm({ ...form, name: e.target.value }); if (errors.name) setErrors({ ...errors, name: null }); }}
                                    className={`w-full h-10 px-3 bg-transparent border rounded-sm text-sm ${errors.name ? "border-primary" : "border-border"}`}
                                    data-testid="invite-name-input" />
                                {errors.name && <div className="text-[11px] text-primary mt-1" data-testid="invite-name-error">{errors.name}</div>}
                            </div>
                            <div>
                                <label className="eyebrow mb-1.5 block">Email</label>
                                <input type="email" value={form.email}
                                    onChange={(e) => { setForm({ ...form, email: e.target.value }); if (errors.email) setErrors({ ...errors, email: null }); }}
                                    onBlur={() => {
                                        const email = form.email?.trim();
                                        if (email && !EMAIL_RE.test(email)) setErrors({ ...errors, email: "Enter a valid email address (e.g. name@company.com)" });
                                    }}
                                    className={`w-full h-10 px-3 bg-transparent border rounded-sm text-sm font-mono ${errors.email ? "border-primary" : "border-border"}`}
                                    data-testid="invite-email-input" />
                                {errors.email && <div className="text-[11px] text-primary mt-1" data-testid="invite-email-error">{errors.email}</div>}
                                {!errors.email && domainOk === false && (
                                    <div className="text-[11px] text-primary mt-1 flex items-start gap-1" data-testid="invite-email-domain-error">
                                        <span aria-hidden>•</span>
                                        <span>{domainReason || "This email domain is not allowed. Use a work email."}</span>
                                    </div>
                                )}
                                {!errors.email && domainOk !== false && domainChecking && (
                                    <div className="text-[11px] text-muted-foreground mt-1" data-testid="invite-email-checking">Checking work email…</div>
                                )}
                            </div>
                            <div>
                                <label className="eyebrow mb-1.5 block">Role</label>
                                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                                    <SelectTrigger className="h-10 rounded-sm" data-testid="invite-role-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {(assignable !== null
                                            ? assignable.map((r) => ({ value: r.code, label: r.name || roleLabel(r.code) }))
                                            : ROLE_OPTIONS_FOR(me?.role)
                                        ).map((o) => (
                                            <SelectItem key={o.value} value={o.value} data-testid={`invite-role-option-${o.value}`}>{o.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {me?.role === "super_admin" && (
                                <>
                                    <div>
                                        <label className="eyebrow mb-1.5 block">Organization</label>
                                        <Select value={form.org_id}
                                            onValueChange={(v) => {
                                                setForm({ ...form, org_id: v, tenant_id: "" });
                                                if (errors.org_id) setErrors({ ...errors, org_id: null });
                                                loadTenantsForOrg(v);
                                            }}>
                                            <SelectTrigger className={`h-10 rounded-sm ${errors.org_id ? "border-primary" : ""}`} data-testid="invite-org-select">
                                                <SelectValue placeholder="Select organization" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {orgs.map((o) => (
                                                    <SelectItem key={o.id} value={o.id}>
                                                        <span className="inline-flex items-center gap-2"><Building2 className="h-3.5 w-3.5 text-muted-foreground" />{o.name}</span>
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        {errors.org_id && <div className="text-[11px] text-primary mt-1" data-testid="invite-org-error">{errors.org_id}</div>}
                                    </div>
                                    <div>
                                        <label className="eyebrow mb-1.5 block">Entity</label>
                                        <Select value={form.tenant_id}
                                            onValueChange={(v) => { setForm({ ...form, tenant_id: v }); if (errors.tenant_id) setErrors({ ...errors, tenant_id: null }); }}
                                            disabled={!form.org_id}>
                                            <SelectTrigger className={`h-10 rounded-sm ${errors.tenant_id ? "border-primary" : ""}`} data-testid="invite-tenant-select">
                                                <SelectValue placeholder={form.org_id ? "Select entity" : "Select an organization first"} />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {(tenantsByOrg[form.org_id] || []).map((t) => (
                                                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        {errors.tenant_id && <div className="text-[11px] text-primary mt-1" data-testid="invite-tenant-error">{errors.tenant_id}</div>}
                                    </div>
                                </>
                            )}
                            {me?.role === "org_admin" && (
                                <>
                                    <div>
                                        <label className="eyebrow mb-1.5 block">Organization</label>
                                        <div className="h-10 px-3 flex items-center gap-2 bg-secondary/40 border border-border rounded-sm text-sm" data-testid="invite-org-readonly">
                                            <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                                            {users.find((u) => u.id === me.id)?.org_name || "Your organization"}
                                        </div>
                                        <p className="text-[11px] text-muted-foreground font-mono mt-1">Invites are scoped to your own organization.</p>
                                    </div>
                                    <div>
                                        <label className="eyebrow mb-1.5 block">Entity</label>
                                        <Select value={form.tenant_id}
                                            onValueChange={(v) => { setForm({ ...form, tenant_id: v }); if (errors.tenant_id) setErrors({ ...errors, tenant_id: null }); }}>
                                            <SelectTrigger className={`h-10 rounded-sm ${errors.tenant_id ? "border-primary" : ""}`} data-testid="invite-tenant-select">
                                                <SelectValue placeholder="Select entity" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {(tenantsByOrg[me.org_id] || []).map((t) => (
                                                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        {errors.tenant_id && <div className="text-[11px] text-primary mt-1" data-testid="invite-tenant-error">{errors.tenant_id}</div>}
                                    </div>
                                </>
                            )}

                            <div className="flex items-start gap-3 p-3 border border-border rounded-sm bg-secondary/30">
                                <Switch checked={!!form.sso_required}
                                    disabled={ssoLoading}
                                    onCheckedChange={(v) => setForm({ ...form, sso_required: v, sso_provider: v && enabledProviders.length === 1 ? enabledProviders[0] : "" })}
                                    data-testid="invite-sso-switch" />
                                <div className="flex-1">
                                    <div className="flex items-center gap-1.5 text-sm font-medium"><ShieldCheck className="h-3.5 w-3.5 text-primary" /> Require SSO sign-in</div>
                                    <div className="text-[11px] text-muted-foreground mt-0.5">
                                        When on, this user must authenticate via the entity's configured SSO provider (Microsoft Entra or Google Workspace). Password login will be disabled for their account.
                                    </div>
                                    {ssoLoading ? (
                                        <div className="text-[11px] text-muted-foreground font-mono mt-1" data-testid="invite-sso-status-checking">Checking SSO for the selected entity…</div>
                                    ) : ssoTarget ? (
                                        <div className="text-[11px] text-muted-foreground font-mono mt-1" data-testid={enabledProviders.length ? "invite-sso-status-active" : "invite-sso-status-inactive"}>
                                            {enabledProviders.length
                                                ? `SSO is active for this entity (${enabledProviders.map(ssoProviderLabel).join(", ")}) — on by default.`
                                                : "SSO isn't configured for this entity — off by default."}
                                        </div>
                                    ) : null}
                                    {form.sso_required && (
                                        <div className="mt-3" data-testid="invite-sso-provider-block">
                                            <label className="eyebrow mb-1.5 block">SSO provider</label>
                                            {enabledProviders.length === 0 ? (
                                                <div className="text-[11px] text-primary font-mono" data-testid="invite-sso-no-providers">
                                                    No SSO provider configured for this entity. Set one up in <b>Settings → SSO</b> first.
                                                </div>
                                            ) : (
                                                <Select value={form.sso_provider || ""}
                                                    onValueChange={(v) => { setForm({ ...form, sso_provider: v }); if (errors.sso_provider) setErrors({ ...errors, sso_provider: null }); }}>
                                                    <SelectTrigger className={`h-9 rounded-sm ${errors.sso_provider ? "border-primary" : ""}`} data-testid="invite-sso-provider-select">
                                                        <SelectValue placeholder={enabledProviders.length === 1 ? ssoProviderLabel(enabledProviders[0]) : "Select an SSO provider"} />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {enabledProviders.map((p) => (
                                                            <SelectItem key={p} value={p} data-testid={`invite-sso-provider-option-${p}`}>
                                                                <span className="inline-flex items-center gap-2">
                                                                    <SsoProviderIcon provider={p} />
                                                                    {ssoProviderLabel(p)}
                                                                </span>
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            )}
                                            {errors.sso_provider && <div className="text-[11px] text-primary mt-1" data-testid="invite-sso-provider-error">{errors.sso_provider}</div>}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => handleOpenChange(false)} className="rounded-sm" data-testid="invite-cancel">Cancel</Button>
                            <Button onClick={submit} disabled={busy || domainChecking || domainOk === false} className="rounded-sm" data-testid="invite-send-button">
                                {busy ? "Sending…" : "Send invitation"}
                            </Button>
                        </DialogFooter>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}
